//src/app/features/questonnaires/list/QuestionnairesTable/index.tsx
import { useState } from 'react';
import useMediaQueries from 'app/hooks/useMediaQueries';
import {
  useAddCartItemProjectMutation,
  useDeleteCartMutation,
} from 'app/services/payments/cart';
import { useLazyGetProjectsQuery } from 'app/services/projects';
import { IProject, ProjectStatusType } from 'app/services/responseTypes';
import { VerticalOptionsIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import OverlayWindow from 'Components/OverlayWindow';
import PaginatedList from 'Components/Pagination/PaginatedList';
import usePagination from 'Components/Pagination/usePagination';
import { useCreatePopup } from 'Components/Popups';
import useCreateModal from 'Components/Popups/useCreateModal';
import { useNavigate } from 'react-router-dom';
import { isEditable } from 'utils/isEditable';
import DeleteModal from './components/DeleteModal';
import DownloadBtn from './components/DownloadBtn';
import ProjectCard from './components/ProjectCard';
import ReuseBtn from './components/reuseBtn';
import TeamAssigneesBtn from './components/TeamAssigneesBtn';
import TeamAssigneesModal from './components/TeamAssigneesModal';

export interface IQuestionnairesTableProps {}

export default function QuestionnairesTable({}: IQuestionnairesTableProps) {
  const { Pagination, data, refetchAfterDelete } = usePagination(
    useLazyGetProjectsQuery,
    {
      filters: {},
      per_page: 4,
    },
  );
  const [DeleteCart] = useDeleteCartMutation();
  const [AddCartItem] = useAddCartItemProjectMutation();

  const { md_down: isMobile } = useMediaQueries();
  const navigate = useNavigate();

  const viewQuestionnaire = (taxReturnToPreview: IProject) => {
    navigate(
      taxReturnToPreview.id.toString() +
        (taxReturnToPreview.businesses?.length > 0
          ? '/businessInfo'
          : '/personal_details'),
    );
  };

  const OptionsPopup = useCreatePopup<IProject>();
  const AssigneesModal = useCreateModal<IProject>();

  return (
    <>
      <PaginatedList
        data={data}
        Pagination={Pagination}
        renderItem={(project) => (
          <ProjectCard
            key={project.id}
            onClick={() => viewQuestionnaire(project)}
            project={project}
          >
            <div className="flex flex-col gap-y-4">
              {isMobile ? (
                <div
                  className="w-10 h-full inline-grid place-items-center p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    OptionsPopup.toggleWithContext({
                      data: project,
                      anchor: e.target as HTMLElement,
                    });
                  }}
                >
                  <VerticalOptionsIcon />
                </div>
              ) : (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="listTableActionsIcons"
                >
                  <TeamAssigneesBtn
                    onClick={() => {
                      AssigneesModal.toggleWithContext({ data: project });
                    }}
                  />
                  <DownloadBtn project={project} />
                  <ReuseBtn project={project} />
                  <DeleteModal
                    project={project}
                    onDelete={refetchAfterDelete}
                  />
                </div>
              )}
              {project.status === 'pending_payment' && (
                <CustomButton
                  textSize="text-xs"
                  height="h-10"
                  bgColor="green"
                  textColor="white"
                  text="Pay"
                  onClick={(e) => {
                    e.stopPropagation();
                    DeleteCart('').then(() => {
                      AddCartItem({
                        cart_item: {
                          product_id: project.id,
                          type: 'project',
                        },
                      }).then(() => {
                        navigate(
                          `/dashboard/questionnaires/${project.id}/checkout`,
                        );
                      });
                    });
                  }}
                />
              )}
            </div>
          </ProjectCard>
        )}
      />

      {isMobile && (
        <OptionsPopup.Component>
          {({ data: project }) => (
            <OverlayWindow size="" paddingSize="">
              <div className="text-sm w-48">
                <TeamAssigneesBtn
                  onClick={() => {
                    OptionsPopup.close();

                    AssigneesModal.openWithContext({ data: project });
                  }}
                />
                <hr className="border-gray-light mx-2" />
                <DownloadBtn project={project!} />
                <hr className="border-gray-light mx-2" />
                <ReuseBtn project={project!} />
                <hr className="border-gray-light mx-2" />
                <DeleteModal
                  project={project!}
                  onDelete={() => {
                    refetchAfterDelete();
                    OptionsPopup.close();
                  }}
                />
              </div>
            </OverlayWindow>
          )}
        </OptionsPopup.Component>
      )}

      <AssigneesModal.Component
        placement={isMobile ? 'stick-to-bottom' : 'screen-centered'}
      >
        {({ data }) => <TeamAssigneesModal taxReturn={data} />}
      </AssigneesModal.Component>
    </>
  );
}
